import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Avatar,
    Box,
    Button,
    CircularProgress,
    Container,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Pagination,
    PaginationItem,
    Divider,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Switch
} from '@mui/material';
import {
    ArrowBackIosNewOutlined,
    SwapHorizRounded,
    SearchRounded,
    CheckCircleRounded,
    WarningRounded,
    ReceiptLongRounded,
    CurrencyRupeeRounded,
    EventNoteRounded,
    SummarizeRounded,
    FilterListRounded,
    HistoryRounded,
    ChevronLeftRounded,
    ChevronRightRounded,
    AddCircleRounded,
    EditRounded,
    CloseRounded,
    SaveRounded,
    AccountBalanceRounded
} from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { globalNotifySuccess, globalNotifyError } from 'views/utilities/AlertToast';
import {
    getMasterAccountRoles,
    getMasterAccounts,
    addDoubleEntry,
    getPaymentTypes,
    getDoubleEntryList,
    addPaymentType,
    updatePaymentType,
    changePaymentTypeStatus
} from 'utils/API/Mis Apis/AccountsApis';

const IDENTIFIER_OPTIONS = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'atacode', label: 'ATA Code' }
];

const INITIAL_ACCOUNT_STATE = {
    role: '',
    identifierType: 'email',
    identifier: '',
    verifiedData: null,
    searchResults: [],
    isVerifying: false
};

const formatRoleLabel = (role) => {
    if (!role) return '';
    return role.replace(/_/g, ' ').charAt(0).toUpperCase() + role.replace(/_/g, ' ').slice(1);
};

const INITIAL_TRANSACTION_STATE = {
    amount: '',
    accounts_head: '',
    date: new Date().toISOString().split('T')[0],
    refrence_no: '',
    remarks: '',
    status: 'received'
};

const validateIdentifier = (type, value) => {
    const trimmed = value.trim();
    if (!trimmed) return false;

    if (type === 'email') {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    }
    if (type === 'phone') {
        return /^[0-9]{10}$/.test(trimmed);
    }
    return trimmed.length >= 3;
};

const AccountSelectorCard = ({ cardKey, title, accentColor, data, roles, onChange, onSearch, onSelectResult }) => {
    const isIdentifierRequired = ['agent', 'distributor'].includes(data.role?.toLowerCase());

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: '16px',
                border: '1px solid',
                borderColor: data.verifiedData ? '#4ade80' : 'divider',
                height: '100%',
                bgcolor: '#fff',
                transition: 'all 0.3s ease-in-out',
                boxShadow: data.verifiedData ? '0 8px 20px rgba(74, 222, 128, 0.1)' : '0 4px 12px rgba(15,23,42,0.03)',
                '&:hover': {
                    borderColor: data.verifiedData ? '#4ade80' : accentColor,
                    boxShadow: data.verifiedData ? '0 12px 28px rgba(74, 222, 128, 0.15)' : `0 12px 24px -8px ${accentColor}20`,
                    transform: 'translateY(-2px)'
                }
            }}
        >
            <Stack spacing={2.5}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 4, height: 16, borderRadius: 1, bgcolor: data.verifiedData ? '#4ade80' : accentColor }} />
                        <Typography variant="subtitle1" fontWeight={800} sx={{ color: 'text.primary', letterSpacing: '-0.01em' }}>
                            {title}
                        </Typography>
                    </Stack>
                    {data.verifiedData && <CheckCircleRounded sx={{ color: '#4ade80', fontSize: 20 }} />}
                </Stack>

                <FormControl fullWidth size="small">
                    <InputLabel id={`${cardKey}-role-label`}>Role / Head</InputLabel>
                    <Select
                        labelId={`${cardKey}-role-label`}
                        label="Role / Head"
                        value={data.role}
                        onChange={(e) => onChange('role', e.target.value)}
                        sx={{ borderRadius: '10px', bgcolor: '#f8fafc' }}
                    >
                        {roles.map((role) => (
                            <MenuItem key={role} value={role}>
                                {formatRoleLabel(role)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {data.role && isIdentifierRequired && (
                    <Stack spacing={1.5}>
                        <FormControl fullWidth size="small">
                            <InputLabel id={`${cardKey}-identifier-type-label`}>Identifier Type</InputLabel>
                            <Select
                                labelId={`${cardKey}-identifier-type-label`}
                                label="Identifier Type"
                                value={data.identifierType}
                                onChange={(e) => onChange('identifierType', e.target.value)}
                                sx={{ borderRadius: '10px', bgcolor: '#f8fafc' }}
                            >
                                {IDENTIFIER_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            size="small"
                            placeholder={`Enter ${
                                IDENTIFIER_OPTIONS.find((opt) => opt.value === data.identifierType)?.label || 'Identifier'
                            }`}
                            value={data.identifier}
                            onChange={(e) => {
                                const val = data.identifierType === 'atacode' ? e.target.value.toUpperCase() : e.target.value;
                                onChange('identifier', val);
                            }}
                            onKeyUp={(e) => {
                                if (e.key === 'Enter' && data.identifier && !data.isVerifying) {
                                    onSearch();
                                }
                            }}
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        onClick={onSearch}
                                        disabled={!data.identifier || data.isVerifying}
                                        size="small"
                                        sx={{ color: accentColor }}
                                    >
                                        {data.isVerifying ? <CircularProgress size={18} /> : <SearchRounded />}
                                    </IconButton>
                                )
                            }}
                            sx={{
                                '& .MuiInputBase-root': {
                                    bgcolor: '#f8fafc',
                                    borderRadius: '10px'
                                }
                            }}
                        />
                    </Stack>
                )}

                {data.searchResults.length > 1 && !data.verifiedData && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            Multiple matches found. Please select:
                        </Typography>
                        <Stack spacing={1}>
                            {data.searchResults.map((result) => (
                                <Box
                                    key={result.id}
                                    onClick={() => onSelectResult(result)}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: '10px',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            borderColor: accentColor,
                                            bgcolor: `${accentColor}05`,
                                            transform: 'translateX(4px)'
                                        }
                                    }}
                                >
                                    <Typography variant="subtitle2" fontWeight={700}>
                                        {result.name || result.user_id}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        ID: {result.user_id} {result.phone ? `• ${result.phone}` : ''}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                )}

                {data.verifiedData && (
                    <Box
                        sx={{
                            p: 1.5,
                            borderRadius: '10px',
                            bgcolor: 'rgba(74, 222, 128, 0.08)',
                            border: '1px dashed #4ade80',
                            animation: 'fadeIn 0.3s ease-out'
                        }}
                    >
                        <Typography variant="subtitle2" fontWeight={800} color="success.dark">
                            {data.verifiedData.name || data.verifiedData.user_id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            ID: {data.verifiedData.user_id} {data.verifiedData.phone ? `• ${data.verifiedData.phone}` : ''}
                        </Typography>
                    </Box>
                )}

                {!data.verifiedData && isIdentifierRequired && !data.isVerifying && data.identifier && data.searchResults.length <= 1 && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <WarningRounded sx={{ color: '#F57200', fontSize: 14 }} />
                        <Typography variant="caption" color="#F57200" fontWeight={600}>
                            Verification required
                        </Typography>
                    </Stack>
                )}
            </Stack>
        </Paper>
    );
};

AccountSelectorCard.propTypes = {
    cardKey: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    accentColor: PropTypes.string.isRequired,
    data: PropTypes.shape({
        role: PropTypes.string,
        identifierType: PropTypes.string,
        identifier: PropTypes.string,
        verifiedData: PropTypes.object,
        searchResults: PropTypes.array,
        isVerifying: PropTypes.bool
    }).isRequired,
    roles: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChange: PropTypes.func.isRequired,
    onSearch: PropTypes.func.isRequired,
    onSelectResult: PropTypes.func.isRequired
};

const PaymentTypeManagementDialog = ({ open, onClose, onRefresh }) => {
    const [paymentHeads, setPaymentHeads] = useState([]);
    const [newItem, setNewItem] = useState('');
    const [editingItem, setEditingItem] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const fetchHeads = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getPaymentTypes({});
            const heads = response?.data?.data;
            if (Array.isArray(heads)) {
                setPaymentHeads(heads);
            }
        } catch (error) {
            console.error('Failed to fetch heads in dialog:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) {
            fetchHeads();
        }
    }, [open, fetchHeads]);

    const handleAdd = async () => {
        if (!newItem.trim()) return;
        setIsLoading(true);
        try {
            const response = await addPaymentType({ payment_type: newItem });
            if (response?.data?.success) {
                globalNotifySuccess(response.data.message || 'Payment type added');
                setNewItem('');
                fetchHeads();
                onRefresh();
            } else {
                globalNotifyError(response?.data?.message || 'Failed to add');
            }
        } catch (error) {
            globalNotifyError('API Error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpdate = async () => {
        if (!editingItem || !editingItem.type.trim()) return;
        setIsLoading(true);
        try {
            const response = await updatePaymentType({ id: editingItem.id, payment_type: editingItem.type });
            if (response?.data?.success) {
                globalNotifySuccess(response.data.message || 'Payment type updated');
                setEditingItem(null);
                fetchHeads();
                onRefresh();
            } else {
                globalNotifyError(response?.data?.message || 'Failed to update');
            }
        } catch (error) {
            globalNotifyError('API Error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggleStatus = async (item) => {
        const newStatus = item.status === 'Active' ? 'Inactive' : 'Active';
        try {
            const response = await changePaymentTypeStatus({ id: item.id, status: newStatus });
            if (response?.data?.success) {
                globalNotifySuccess(response.data.message || 'Status updated');
                fetchHeads();
                onRefresh();
            }
        } catch (error) {
            globalNotifyError('API Error');
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '24px',
                    bgcolor: 'rgba(255, 255, 255, 0.7)',
                    backdropFilter: 'blur(20px) saturate(180%)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)'
                }
            }}
        >
            <DialogTitle sx={{ pb: 1, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ bgcolor: 'primary.50', color: 'primary.main', width: 32, height: 32 }}>
                            <AccountBalanceRounded sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Typography variant="h5" fontWeight={800}>
                            Manage Account Heads
                        </Typography>
                    </Stack>
                    <IconButton onClick={onClose} size="small" sx={{ bgcolor: 'rgba(0,0,0,0.03)' }}>
                        <CloseRounded fontSize="small" />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ py: 3 }}>
                <Stack spacing={3}>
                    {/* Add Form */}
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '16px',
                            bgcolor: 'rgba(255,255,255,0.4)',
                            border: '1px solid rgba(255,255,255,0.5)'
                        }}
                    >
                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1, ml: 0.5 }}>
                            ADD NEW ACCOUNT HEAD
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="e.g. MATM Payment"
                                value={newItem}
                                onChange={(e) => setNewItem(e.target.value)}
                                sx={{ '& .MuiInputBase-root': { borderRadius: '10px', bgcolor: '#fff' } }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleAdd}
                                disabled={!newItem || isLoading}
                                startIcon={isLoading ? <CircularProgress size={16} /> : <AddCircleRounded />}
                                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, px: 3 }}
                            >
                                Add
                            </Button>
                        </Stack>
                    </Box>

                    {/* List */}
                    <Box>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1.5, ml: 0.5 }}>
                            EXISTING ACCOUNT HEADS ({paymentHeads.length})
                        </Typography>
                        <List
                            sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.3)', p: 0 }}
                        >
                            {paymentHeads.map((item, index) => (
                                <ListItem
                                    key={item.id}
                                    divider={index !== paymentHeads.length - 1}
                                    sx={{
                                        py: 1.5,
                                        px: 2.5,
                                        transition: 'background 0.2s',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.4)' }
                                    }}
                                >
                                    {editingItem?.id === item.id ? (
                                        <Stack direction="row" spacing={1} sx={{ width: '100%', alignItems: 'center' }}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={editingItem.type}
                                                onChange={(e) => setEditingItem({ ...editingItem, type: e.target.value })}
                                                sx={{ '& .MuiInputBase-root': { borderRadius: '8px', bgcolor: '#fff' } }}
                                            />
                                            <IconButton onClick={handleUpdate} size="small" color="primary">
                                                <SaveRounded fontSize="small" />
                                            </IconButton>
                                            <IconButton onClick={() => setEditingItem(null)} size="small">
                                                <CloseRounded fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    ) : (
                                        <>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="subtitle2" fontWeight={700}>
                                                        {item.type}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Chip
                                                        label={item.status}
                                                        size="small"
                                                        color={item.status === 'active' ? 'success' : 'default'}
                                                        sx={{ height: 16, fontSize: '0.6rem', fontWeight: 800, mt: 0.5 }}
                                                    />
                                                }
                                            />
                                            <ListItemSecondaryAction>
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <IconButton
                                                        onClick={() => setEditingItem(item)}
                                                        size="small"
                                                        sx={{ color: 'primary.main' }}
                                                    >
                                                        <EditRounded fontSize="small" />
                                                    </IconButton>
                                                    <Switch
                                                        size="small"
                                                        checked={item.status === 'Active'}
                                                        onChange={() => handleToggleStatus(item)}
                                                        color="success"
                                                    />
                                                </Stack>
                                            </ListItemSecondaryAction>
                                        </>
                                    )}
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

PaymentTypeManagementDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onRefresh: PropTypes.func.isRequired
};

const DoubleEntryManagement = () => {
    const navigate = useNavigate();
    const [roles, setRoles] = useState([]);
    const [isRolesLoading, setIsRolesLoading] = useState(true);
    const [rolesError, setRolesError] = useState('');
    const [fromAccount, setFromAccount] = useState(INITIAL_ACCOUNT_STATE);
    const [toAccount, setToAccount] = useState(INITIAL_ACCOUNT_STATE);
    const [transaction, setTransaction] = useState(INITIAL_TRANSACTION_STATE);
    const [paymentHeads, setPaymentHeads] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isHeadsLoading, setIsHeadsLoading] = useState(false);
    const [isHeadDialogOpen, setIsHeadDialogOpen] = useState(false);

    const reportSectionRef = useRef(null);

    // Entries List State
    const [entries, setEntries] = useState([]);
    const [isEntriesLoading, setIsEntriesLoading] = useState(false);
    const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0, per_page: 4 });
    const [filters, setFilters] = useState({
        from_date: new Date().toISOString().split('T')[0],
        to_date: new Date().toISOString().split('T')[0],
        status: '',
        refrence_no: '',
        role: ''
    });

    // Refs to track latest state for stable handleSearch
    const fromAccountRef = useRef(fromAccount);
    const toAccountRef = useRef(toAccount);

    useEffect(() => {
        fromAccountRef.current = fromAccount;
    }, [fromAccount]);

    useEffect(() => {
        toAccountRef.current = toAccount;
    }, [toAccount]);

    useEffect(() => {
        const fetchRoles = async () => {
            setIsRolesLoading(true);
            setRolesError('');
            try {
                const response = await getMasterAccountRoles();
                const roleList = response?.data?.data;

                if (Array.isArray(roleList) && roleList.length > 0) {
                    setRoles(roleList);
                    return;
                }
                setRolesError('Unable to load account roles. Please refresh and try again.');
            } catch (error) {
                console.error('Role fetch failed:', error);
                setRolesError('Unable to load account roles. Please refresh and try again.');
            } finally {
                setIsRolesLoading(false);
            }
        };

        fetchRoles();
    }, []);

    useEffect(() => {
        const fetchPaymentHeads = async () => {
            setIsHeadsLoading(true);
            try {
                const response = await getPaymentTypes({ status: 'Active' });
                const heads = response?.data?.data;
                if (Array.isArray(heads)) {
                    setPaymentHeads(heads);
                }
            } catch (error) {
                console.error('Failed to fetch payment heads:', error);
            } finally {
                setIsHeadsLoading(false);
            }
        };

        fetchPaymentHeads();
    }, []);

    const refreshPaymentHeads = useCallback(async () => {
        setIsHeadsLoading(true);
        try {
            const response = await getPaymentTypes({ status: 'Active' });
            const heads = response?.data?.data;
            if (Array.isArray(heads)) {
                setPaymentHeads(heads);
            }
        } catch (error) {
            console.error('Failed to fetch payment heads:', error);
        } finally {
            setIsHeadsLoading(false);
        }
    }, []);

    const fetchEntries = useCallback(
        async (page = 1) => {
            setIsEntriesLoading(true);
            try {
                const payload = {
                    ...filters,
                    page: String(page),
                    per_page: '20'
                };
                const response = await getDoubleEntryList(payload);
                if (response?.data?.success) {
                    setEntries(response.data.data || []);
                    if (response.data.pagination) {
                        setPagination(response.data.pagination);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch entries:', error);
            } finally {
                setIsEntriesLoading(false);
            }
        },
        [filters]
    );

    useEffect(() => {
        fetchEntries(1);
    }, [fetchEntries]);

    const handleSearch = useCallback(async (side) => {
        const account = side === 'from' ? fromAccountRef.current : toAccountRef.current;
        const setAccount = side === 'from' ? setFromAccount : setToAccount;

        if (!account.role) return;

        // Skip if already verifying or already verified for this role (internal heads)
        if (account.isVerifying) return;

        const roleLower = account.role.toLowerCase();
        const requiresIdentifier = ['agent', 'distributor'].includes(roleLower);

        // Extra guard for auto-trigger: if we already have verified data for this internal role, skip
        if (!requiresIdentifier && account.verifiedData && account.verifiedData.user_type === account.role) {
            return;
        }

        if (requiresIdentifier && !account.identifier) {
            globalNotifyError('Identifier is required for this role.');
            return;
        }

        setAccount((prev) => ({ ...prev, isVerifying: true, verifiedData: null, searchResults: [] }));
        try {
            const payload = {
                user_type: account.role,
                [account.identifierType === 'email' ? 'email' : account.identifierType === 'phone' ? 'phone' : 'atacode']:
                    account.identifier
            };

            const response = await getMasterAccounts(payload);
            const results = response?.data?.data || [];

            if (response?.data?.success && results.length > 0) {
                if (results.length === 1) {
                    setAccount((prev) => ({ ...prev, verifiedData: results[0], searchResults: [] }));
                    globalNotifySuccess(`${side === 'from' ? 'Source' : 'Destination'} account verified.`);
                } else {
                    setAccount((prev) => ({ ...prev, searchResults: results, verifiedData: null }));
                    globalNotifySuccess(`Multiple ${side === 'from' ? 'source' : 'destination'} accounts found.`);
                }
            } else {
                globalNotifyError('Account not found. Please check identifiers.');
            }
        } catch (error) {
            console.error('Account search failed:', error);
            globalNotifyError('Failed to verify account.');
        } finally {
            setAccount((prev) => ({ ...prev, isVerifying: false }));
        }
    }, []);

    const handleSelectResult = (side, result) => {
        const setAccount = side === 'from' ? setFromAccount : setToAccount;
        setAccount((prev) => ({ ...prev, verifiedData: result, searchResults: [] }));
        globalNotifySuccess(`${side === 'from' ? 'Source' : 'Destination'} account selected.`);
    };

    useEffect(() => {
        if (fromAccount.role && !['agent', 'distributor'].includes(fromAccount.role.toLowerCase())) {
            handleSearch('from');
        }
    }, [fromAccount.role, handleSearch]);

    useEffect(() => {
        if (toAccount.role && !['agent', 'distributor'].includes(toAccount.role.toLowerCase())) {
            handleSearch('to');
        }
    }, [toAccount.role, handleSearch]);

    const handleFromChange = (field, value) => {
        setFromAccount((prev) => ({
            ...prev,
            [field]: value,
            ...(field === 'role' || field === 'identifier' || field === 'identifierType' ? { verifiedData: null, searchResults: [] } : {}),
            ...(field === 'role' ? { identifierType: 'email', identifier: '' } : {}),
            ...(field === 'identifierType' ? { identifier: '' } : {})
        }));
    };

    const handleToChange = (field, value) => {
        setToAccount((prev) => ({
            ...prev,
            [field]: value,
            ...(field === 'role' || field === 'identifier' || field === 'identifierType' ? { verifiedData: null, searchResults: [] } : {}),
            ...(field === 'role' ? { identifierType: 'email', identifier: '' } : {}),
            ...(field === 'identifierType' ? { identifier: '' } : {})
        }));
    };

    const isFormValid = useMemo(() => {
        if (!fromAccount.role || !toAccount.role) return false;
        if (!transaction.amount || parseFloat(transaction.amount) < 1) return false;
        if (!transaction.accounts_head || !transaction.date) return false;

        const requiresFrom = ['agent', 'distributor'].includes(fromAccount.role.toLowerCase());
        const requiresTo = ['agent', 'distributor'].includes(toAccount.role.toLowerCase());

        if (requiresFrom && (!fromAccount.verifiedData || !validateIdentifier(fromAccount.identifierType, fromAccount.identifier)))
            return false;
        if (requiresTo && (!toAccount.verifiedData || !validateIdentifier(toAccount.identifierType, toAccount.identifier))) return false;

        return true;
    }, [fromAccount, toAccount, transaction]);

    const handleTransactionChange = (field, value) => {
        if (field === 'amount') {
            // Allow numbers with up to 4 decimal places
            const regex = /^\d*\.?\d{0,4}$/;
            if (value !== '' && !regex.test(value)) {
                return;
            }
        }
        setTransaction((prev) => ({ ...prev, [field]: value }));
    };

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async () => {
        if (!isFormValid) return;

        setIsSubmitting(true);
        try {
            const payload = {
                from_id: fromAccount.verifiedData?.id || (roles.includes(fromAccount.role) ? fromAccount.role : null),
                to_id: toAccount.verifiedData?.id || (roles.includes(toAccount.role) ? toAccount.role : null),
                amount: parseFloat(transaction.amount),
                accounts_head: transaction.accounts_head,
                date_of_txn: transaction.date,
                refrence_no: transaction.refrence_no,
                remarks: transaction.remarks,
                status: transaction.status
            };

            const response = await addDoubleEntry(payload);
            if (response?.data?.success) {
                globalNotifySuccess(response.data.message || 'Double entry successfully entered.');
                setTransaction((prev) => ({ ...INITIAL_TRANSACTION_STATE, date: prev.date }));
                fetchEntries(1); // Refresh the list

                // Smooth scroll to report section
                setTimeout(() => {
                    reportSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 500);
            } else {
                globalNotifyError(response?.data?.message || 'Failed to submit double entry.');
            }
        } catch (error) {
            console.error('Double entry submit failed:', error);
            globalNotifyError('Failed to submit double entry. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                pb: 6,
                background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)'
            }}
        >
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #1976d2 60%, #42a5f5 100%)',
                    px: { xs: 2, md: 4 },
                    pt: 4,
                    pb: 11,
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 100%)',
                        pointerEvents: 'none'
                    }}
                />
                {/* Decorative circles */}
                {[
                    { size: 280, top: -100, right: -80, op: 0.08 },
                    { size: 160, top: 20, right: 140, op: 0.06 }
                ].map((c, i) => (
                    <Box
                        key={i}
                        sx={{
                            position: 'absolute',
                            top: c.top,
                            right: c.right,
                            width: c.size,
                            height: c.size,
                            borderRadius: '50%',
                            bgcolor: '#fff',
                            opacity: c.op
                        }}
                    />
                ))}

                <IconButton
                    onClick={() => navigate('/accounts/dashboard')}
                    sx={{
                        color: '#fff',
                        mb: 2,
                        bgcolor: 'rgba(255,255,255,0.12)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                        backdropFilter: 'blur(8px)'
                    }}
                    size="small"
                >
                    <ArrowBackIosNewOutlined fontSize="small" />
                </IconButton>

                <Box>
                    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.5 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4ade80', boxShadow: '0 0 10px #4ade80' }} />
                        <Typography
                            variant="overline"
                            sx={{ color: 'rgba(255,255,255,0.8)', letterSpacing: '0.15em', fontWeight: 700, fontSize: '0.75rem' }}
                        >
                            CONFIG CONSOLE
                        </Typography>
                    </Stack>
                    <Typography variant="h3" fontWeight={800} sx={{ color: '#fff', letterSpacing: '-0.02em' }}>
                        Double Entry Management
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1, maxWidth: 600 }}>
                        Precisely configure source and destination accounts to automate complex double-entry accounting workflows.
                    </Typography>
                </Box>
            </Box>

            <Container maxWidth="lg" sx={{ mt: -4, position: 'relative', zIndex: 1 }}>
                <Paper
                    elevation={6}
                    sx={{
                        p: { xs: 3, md: 4 },
                        borderRadius: '24px',
                        bgcolor: '#fff',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.12)'
                    }}
                >
                    {/* Entry Mapping Section */}
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 4 }}>
                        <Avatar sx={{ bgcolor: 'primary.50', color: 'primary.main' }}>
                            <SwapHorizRounded />
                        </Avatar>
                        <Box>
                            <Typography variant="h5" fontWeight={800} color="text.primary">
                                Step 1: Account Selection
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Source and destination identifiers configuration
                            </Typography>
                        </Box>
                    </Stack>

                    {rolesError && (
                        <Alert severity="error" variant="outlined" sx={{ mb: 4, borderRadius: '12px' }}>
                            {rolesError}
                        </Alert>
                    )}

                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <AccountSelectorCard
                                cardKey="from-account"
                                title="Source (From) Account"
                                accentColor="#1e40af"
                                data={fromAccount}
                                roles={roles}
                                onChange={handleFromChange}
                                onSearch={() => handleSearch('from')}
                                onSelectResult={(res) => handleSelectResult('from', res)}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <AccountSelectorCard
                                cardKey="to-account"
                                title="Destination (To) Account"
                                accentColor="#0369a1"
                                data={toAccount}
                                roles={roles}
                                onChange={handleToChange}
                                onSearch={() => handleSearch('to')}
                                onSelectResult={(res) => handleSelectResult('to', res)}
                            />
                        </Grid>
                    </Grid>

                    <Box
                        sx={{
                            mt: 5,
                            mb: 4,
                            pt: 3,
                            borderTop: '1px solid',
                            borderColor: 'grey.100'
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 4 }}>
                            <Avatar sx={{ bgcolor: 'success.50', color: 'success.main' }}>
                                <ReceiptLongRounded />
                            </Avatar>
                            <Box>
                                <Typography variant="h5" fontWeight={800} color="text.primary">
                                    Step 2: Transaction Details
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Finalize entries, amount, and payment head
                                </Typography>
                            </Box>
                        </Stack>

                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Amount"
                                    type="number"
                                    required
                                    value={transaction.amount}
                                    onChange={(e) => handleTransactionChange('amount', e.target.value)}
                                    placeholder="Enter amount in ₹"
                                    InputProps={{
                                        startAdornment: <CurrencyRupeeRounded sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />,
                                        sx: { borderRadius: '12px' }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="payment-head-label">Account Head *</InputLabel>
                                    <Select
                                        labelId="payment-head-label"
                                        label="Account Head"
                                        required
                                        value={transaction.accounts_head}
                                        onChange={(e) => handleTransactionChange('accounts_head', e.target.value)}
                                        sx={{ borderRadius: '12px' }}
                                        endAdornment={
                                            <IconButton
                                                size="small"
                                                sx={{ mr: 2, color: 'primary.main' }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsHeadDialogOpen(true);
                                                }}
                                            >
                                                <AddCircleRounded />
                                            </IconButton>
                                        }
                                    >
                                        {paymentHeads.map((head) => (
                                            <MenuItem key={head.id} value={head.id}>
                                                {head.type}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    label="Posting Date"
                                    type="date"
                                    value={transaction.date}
                                    onChange={(e) => handleTransactionChange('date', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        startAdornment: <EventNoteRounded sx={{ mr: 1, color: 'text.secondary' }} />,
                                        sx: { borderRadius: '12px' }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    fullWidth
                                    label="Reference Number"
                                    value={transaction.refrence_no}
                                    onChange={(e) => handleTransactionChange('refrence_no', e.target.value)}
                                    placeholder="Optional"
                                    InputProps={{ sx: { borderRadius: '12px' } }}
                                />
                            </Grid>
                            {/* <Grid item xs={12} sm={6} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel id="status-label">Entry Status</InputLabel>
                                    <Select
                                        labelId="status-label"
                                        label="Entry Status"
                                        value={transaction.status}
                                        onChange={(e) => handleTransactionChange('status', e.target.value)}
                                        sx={{ borderRadius: '12px' }}
                                    >
                                        <MenuItem value="received">Received</MenuItem>
                                        <MenuItem value="not received">Not Received</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid> */}
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={1}
                                    label="Remarks"
                                    value={transaction.remarks}
                                    onChange={(e) => handleTransactionChange('remarks', e.target.value)}
                                    placeholder="Brief description..."
                                    InputProps={{
                                        startAdornment: <SummarizeRounded sx={{ mr: 1, color: 'text.secondary' }} />,
                                        sx: { borderRadius: '12px' }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Final Actions */}
                    <Box
                        sx={{
                            mt: 5,
                            pt: 4,
                            borderTop: '1px solid',
                            borderColor: 'grey.100',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 3
                        }}
                    >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: isFormValid ? '#4ade80' : 'warning.main' }} />
                            <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                {isFormValid ? 'All requirements met. Ready to post entry.' : 'Review required fields to enable posting.'}
                            </Typography>
                        </Stack>

                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleSubmit}
                            disabled={!isFormValid || isSubmitting || isRolesLoading || isHeadsLoading}
                            sx={{
                                background: 'linear-gradient(135deg, #1e3a8a 0%, #1976d2 100%)',
                                color: '#fff',
                                fontWeight: 800,
                                borderRadius: '14px',
                                px: 5,
                                py: 2,
                                textTransform: 'none',
                                fontSize: '1rem',
                                boxShadow: '0 10px 25px rgba(25, 118, 210, 0.25)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 15px 35px rgba(25, 118, 210, 0.35)'
                                },
                                '&.Mui-disabled': {
                                    bgcolor: 'grey.200',
                                    color: 'grey.500'
                                },
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        >
                            {isSubmitting ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                <>
                                    Add Double Entry
                                    <Box
                                        component="span"
                                        sx={{
                                            ml: 1.5,
                                            display: 'inline-flex',
                                            bgcolor: 'rgba(255,255,255,0.2)',
                                            borderRadius: '50%',
                                            p: 0.5
                                        }}
                                    >
                                        <SwapHorizRounded fontSize="small" />
                                    </Box>
                                </>
                            )}
                        </Button>
                    </Box>
                </Paper>

                {/* Double Entry Records Section */}
                <Paper
                    ref={reportSectionRef}
                    elevation={6}
                    sx={{
                        p: { xs: 3, md: 4 },
                        mt: 4,
                        borderRadius: '24px',
                        bgcolor: '#fff',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.12)',
                        mb: 6
                    }}
                >
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={2}
                        sx={{ mb: 4 }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Avatar sx={{ bgcolor: 'secondary.50', color: 'secondary.main' }}>
                                <HistoryRounded />
                            </Avatar>
                            <Box>
                                <Typography variant="h5" fontWeight={800} color="text.primary">
                                    Double Entry Records
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    History of recorded transactions
                                </Typography>
                            </Box>
                        </Stack>

                        <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                            <Button
                                startIcon={<FilterListRounded />}
                                variant="outlined"
                                color="secondary"
                                onClick={() => fetchEntries(1)}
                                sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600 }}
                            >
                                Apply Filters
                            </Button>
                        </Stack>
                    </Stack>

                    {/* Filters Grid */}
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                label="From Date"
                                type="date"
                                value={filters.from_date}
                                onChange={(e) => handleFilterChange('from_date', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ sx: { borderRadius: '12px' } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                label="To Date"
                                type="date"
                                value={filters.to_date}
                                onChange={(e) => handleFilterChange('to_date', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ sx: { borderRadius: '12px' } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth>
                                <InputLabel id="filter-status-label">Status</InputLabel>
                                <Select
                                    labelId="filter-status-label"
                                    label="Status"
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                    sx={{ borderRadius: '12px' }}
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    <MenuItem value="received">Received</MenuItem>
                                    <MenuItem value="not received">Not Received</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth>
                                <InputLabel id="filter-role-label">Filter by Role</InputLabel>
                                <Select
                                    labelId="filter-role-label"
                                    label="Filter by Role"
                                    value={filters.role}
                                    onChange={(e) => handleFilterChange('role', e.target.value)}
                                    sx={{ borderRadius: '12px' }}
                                >
                                    <MenuItem value="">All Roles</MenuItem>
                                    {roles.map((role) => (
                                        <MenuItem key={role} value={role}>
                                            {formatRoleLabel(role)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                label="Reference No"
                                value={filters.refrence_no}
                                onChange={(e) => handleFilterChange('refrence_no', e.target.value)}
                                placeholder="Search ref..."
                                InputProps={{ sx: { borderRadius: '12px' } }}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ mb: 4 }} />

                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table sx={{ minWidth: 800 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', bgcolor: 'grey.50' }}>
                                        REFERENCE & AUDIT
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', bgcolor: 'grey.50' }}>
                                        USER INFORMATION
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', bgcolor: 'grey.50' }}>ROLE & CORE</TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', bgcolor: 'grey.50' }}>
                                        ENTRY DETAILS
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', bgcolor: 'grey.50' }}>AMOUNT</TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', bgcolor: 'grey.50' }}>DATE</TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', bgcolor: 'grey.50' }}>STATUS</TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', bgcolor: 'grey.50' }}>REMARKS</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isEntriesLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                            <CircularProgress size={32} />
                                            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                                Fetching records...
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : entries.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                            <Typography variant="body1" fontWeight={600} color="text.secondary">
                                                No records found for the selected period.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    entries.map((row) => (
                                        <TableRow key={row.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={800} color="primary.main">
                                                    {row.refrence_no || 'NO_REF'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Created:{' '}
                                                    {new Date(row.created_at).toLocaleString('en-GB', {
                                                        day: '2-digit',
                                                        month: 'short',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={700}>
                                                    {row.master_name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {row.master_phone}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    display="block"
                                                    sx={{ fontSize: '0.65rem' }}
                                                >
                                                    {row.master_email}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.master_user_type}
                                                    size="small"
                                                    sx={{
                                                        height: 18,
                                                        fontSize: '0.65rem',
                                                        textTransform: 'uppercase',
                                                        fontWeight: 800,
                                                        mb: 0.5
                                                    }}
                                                />
                                                <Typography variant="body2" fontWeight={600} color="text.secondary">
                                                    {row.master_atacode}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={700}>
                                                    {row.payment_type_name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                                                    Mode: {row.payment_mode}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={900} color="text.primary">
                                                    ₹
                                                    {parseFloat(row.amount).toLocaleString('en-IN', {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 4
                                                    })}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {new Date(row.date_of_txn).toLocaleDateString('en-GB', {
                                                        day: '2-digit',
                                                        month: 'short',
                                                        year: 'numeric'
                                                    })}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.status}
                                                    size="small"
                                                    color={row.status === 'received' ? 'success' : 'warning'}
                                                    variant="light"
                                                    sx={{ fontWeight: 800, textTransform: 'capitalize' }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={row.remarks || 'No remarks'}>
                                                    <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 120 }}>
                                                        {row.remarks || '--'}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 4 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            Showing {entries.length} of {pagination.total} records
                        </Typography>
                        <Pagination
                            count={pagination.last_page}
                            page={pagination.current_page}
                            onChange={(e, page) => fetchEntries(page)}
                            color="primary"
                            shape="rounded"
                            size="medium"
                            renderItem={(item) => (
                                <PaginationItem
                                    slots={{ previous: ChevronLeftRounded, next: ChevronRightRounded }}
                                    {...item}
                                    sx={{ borderRadius: '8px' }}
                                />
                            )}
                        />
                    </Stack>
                </Paper>
            </Container>

            <PaymentTypeManagementDialog
                open={isHeadDialogOpen}
                onClose={() => setIsHeadDialogOpen(false)}
                onRefresh={refreshPaymentHeads}
            />
        </Box>
    );
};

export default DoubleEntryManagement;
